import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Plus, FileText, Receipt, User, Phone, Mail, CreditCard, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '../utils/api';
import { format, addDays, differenceInDays, parseISO, eachDayOfInterval, isWeekend, getDay } from 'date-fns';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Common countries list
const countries = [
  { code: 'MY', name: 'Malaysia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'OTHER', name: 'Other' },
];

// Malaysian states list
const malaysianStates = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 
  'Penang', 'Perak', 'Perlis', 'Sabah', 'Sarawak', 'Selangor', 
  'Terengganu', 'Kuala Lumpur', 'Labuan', 'Putrajaya', 'Not Applicable'
];

// E-Invoice country codes
const einvoiceCountryCodes = [
  { code: 'MYS', name: 'Malaysia' },
  { code: 'SGP', name: 'Singapore' },
  { code: 'IDN', name: 'Indonesia' },
  { code: 'THA', name: 'Thailand' },
  { code: 'PHL', name: 'Philippines' },
  { code: 'VNM', name: 'Vietnam' },
  { code: 'BRN', name: 'Brunei' },
  { code: 'CHN', name: 'China' },
  { code: 'HKG', name: 'Hong Kong' },
  { code: 'TWN', name: 'Taiwan' },
  { code: 'JPN', name: 'Japan' },
  { code: 'KOR', name: 'South Korea' },
  { code: 'IND', name: 'India' },
  { code: 'AUS', name: 'Australia' },
  { code: 'NZL', name: 'New Zealand' },
  { code: 'GBR', name: 'United Kingdom' },
  { code: 'USA', name: 'United States' },
];

const CreateBookingModal = ({ 
  open, 
  onClose, 
  properties = [], 
  rooms = [], 
  onSuccess,
  preSelectedRoom = null,
  preSelectedDates = null 
}) => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Form state
  const [activeTab, setActiveTab] = useState('details');
  const [saving, setSaving] = useState(false);
  
  // Guest and booking details
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_country: 'MY',
    property_id: '',
    room_type: '',
    room_id: '',
    check_in: format(new Date(), 'yyyy-MM-dd'),
    check_out: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    total_price: 0,
    adults: 2,
    notes: '',
    // E-Invoice fields
    e_invoice_required: false,
    einvoice_buyer_name: '',
    einvoice_buyer_tin: '',
    einvoice_buyer_id_type: 'NRIC',
    einvoice_buyer_id_value: '',
    einvoice_buyer_sst_no: 'NA',
    einvoice_buyer_email: '',
    einvoice_buyer_phone: '',
    einvoice_buyer_address: '',
    einvoice_buyer_city: '',
    einvoice_buyer_state: '',
    einvoice_buyer_postal_code: '',
    einvoice_buyer_country: 'MYS'
  });
  
  // Charges and Payments
  const [charges, setCharges] = useState([]);
  const [payments, setPayments] = useState([]);
  const [newChargeForm, setNewChargeForm] = useState({ type: 'room_charges', description: '', amount: 0, quantity: 1 });
  const [newPaymentForm, setNewPaymentForm] = useState({ method: 'cash', amount: 0, reference: '', cashReference: '' });
  
  // State for tracking selected charges for payment
  const [selectedChargesForPayment, setSelectedChargesForPayment] = useState([]);
  
  // Calculate total of selected charges
  const selectedChargesTotal = useMemo(() => {
    return charges
      .filter(c => selectedChargesForPayment.includes(c.id))
      .reduce((sum, c) => sum + (c.total || c.amount), 0);
  }, [charges, selectedChargesForPayment]);
  
  // Toggle charge selection for payment
  const toggleChargeForPayment = (chargeId, chargeAmount) => {
    setSelectedChargesForPayment(prev => {
      const isSelected = prev.includes(chargeId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== chargeId)
        : [...prev, chargeId];
      
      // Auto-update payment amount based on selected charges
      const newTotal = charges
        .filter(c => newSelection.includes(c.id))
        .reduce((sum, c) => sum + (c.total || c.amount), 0);
      
      setNewPaymentForm(form => ({ ...form, amount: newTotal }));
      
      return newSelection;
    });
  };
  
  // Link to Items state for payment recording - initialized without nights dependency
  const [linkedItems, setLinkedItems] = useState({
    room_charges: { checked: false, qty: 1, rate: 0 },
    security_deposit: { checked: false, qty: 1, rate: 200 },
    tourism_tax: { checked: false, qty: 1, rate: 10 },
    hotel_fee: { checked: false, qty: 1, rate: 2 },
    early_checkin: { checked: false, qty: 1, rate: 15 },
    late_checkout: { checked: false, qty: 1, rate: 50 },
    printing: { checked: false, qty: 1, rate: 1 },
    vip_parking: { checked: false, qty: 1, rate: 20 },
    wheelchair: { checked: false, qty: 1, rate: 8 },
    miscellaneous: { checked: false, qty: 1, rate: 0 }
  });
  
  // Counter rates (by room_id)
  const [counterRates, setCounterRates] = useState([]);
  
  // Local state for properties and rooms if not passed as props
  const [localProperties, setLocalProperties] = useState([]);
  const [localRooms, setLocalRooms] = useState([]);
  
  // State for existing bookings (for availability check)
  const [existingBookings, setExistingBookings] = useState([]);
  
  // Use props if provided, otherwise use local state
  const effectiveProperties = properties.length > 0 ? properties : localProperties;
  const effectiveRooms = rooms.length > 0 ? rooms : localRooms;
  
  // Fetch properties and rooms when modal opens if not provided as props
  useEffect(() => {
    if (open) {
      // Fetch properties if not provided
      if (properties.length === 0) {
        api.get(`${API}/properties`)
          .then(res => setLocalProperties(res.data || []))
          .catch(err => console.error('Failed to fetch properties:', err));
      }
      // Fetch rooms if not provided
      if (rooms.length === 0) {
        api.get(`${API}/rooms`)
          .then(res => setLocalRooms(res.data || []))
          .catch(err => console.error('Failed to fetch rooms:', err));
      }
      // Fetch counter rates (stored by room_id)
      api.get(`${API}/counter-rates`)
        .then(res => {
          const rates = Array.isArray(res.data) ? res.data : [];
          console.log('Counter rates loaded:', rates);
          setCounterRates(rates);
        })
        .catch(err => console.error('Failed to fetch counter rates:', err));
      
      // Fetch existing bookings for availability check - get bookings around the selected dates
      const fetchBookingsForAvailability = async () => {
        try {
          // Get bookings from 30 days before to 60 days after to cover most scenarios
          const today = new Date();
          const fromDate = format(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          const toDate = format(new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          const res = await api.get(`${API}/bookings?from_date=${fromDate}&to_date=${toDate}&limit=5000`);
          setExistingBookings(res.data?.bookings || res.data || []);
        } catch (err) {
          console.error('Failed to fetch bookings:', err);
        }
      };
      fetchBookingsForAvailability();
    }
  }, [open, properties.length, rooms.length]);
  
  // Filter properties for Create Booking - only show properties with available rooms for selected dates
  const createBookingProperties = useMemo(() => {
    // Natural sort function to handle numbers in property names correctly
    const naturalSort = (a, b) => {
      const regex = /(\d+)|(\D+)/g;
      const aParts = a.name.match(regex) || [];
      const bParts = b.name.match(regex) || [];
      
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || '';
        const bPart = bParts[i] || '';
        
        const aNum = parseInt(aPart, 10);
        const bNum = parseInt(bPart, 10);
        
        // Both are numbers
        if (!isNaN(aNum) && !isNaN(bNum)) {
          if (aNum !== bNum) return aNum - bNum;
        } else {
          // Compare as strings
          const cmp = aPart.localeCompare(bPart);
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    };
    
    // Helper to get unit name for a property
    const getPropertyUnitName = (propertyId) => {
      const propertyRooms = effectiveRooms.filter(r => r.property_id === propertyId);
      if (propertyRooms.length > 0 && propertyRooms[0].unit_name) {
        return propertyRooms[0].unit_name;
      }
      return null;
    };
    
    // Filter out "Mansion One Suites By Merlene" property
    const filteredProperties = effectiveProperties.filter(p => 
      !p.name?.toLowerCase().includes('mansion one suites')
    );
    
    // If no dates selected, show all properties with unit names
    if (!formData.check_in || !formData.check_out) {
      return [...filteredProperties].map(p => ({
        ...p,
        displayName: getPropertyUnitName(p.id) ? `${p.name} (${getPropertyUnitName(p.id)})` : p.name
      })).sort(naturalSort);
    }
    
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    
    // Filter properties that have at least one available room
    const availableProperties = filteredProperties.filter(property => {
      // Get all rooms for this property
      const propertyRooms = effectiveRooms.filter(r => r.property_id === property.id);
      
      if (propertyRooms.length === 0) return false;
      
      // Check if at least one room is available
      return propertyRooms.some(room => {
        // Check for conflicting bookings
        const hasConflict = existingBookings.some(booking => {
          if (booking.room_id !== room.id) return false;
          if (booking.status === 'cancelled' || booking.status === 'no_show') return false;
          
          const bookingCheckIn = new Date(booking.check_in);
          const bookingCheckOut = new Date(booking.check_out);
          
          // Overlap: newCheckIn < existingCheckOut AND newCheckOut > existingCheckIn
          return checkIn < bookingCheckOut && checkOut > bookingCheckIn;
        });
        
        return !hasConflict;
      });
    });
    
    // Add unit name to each property for display
    return availableProperties.map(p => ({
      ...p,
      displayName: getPropertyUnitName(p.id) ? `${p.name} (${getPropertyUnitName(p.id)})` : p.name
    })).sort(naturalSort);
  }, [effectiveProperties, effectiveRooms, existingBookings, formData.check_in, formData.check_out]);
  
  // Filter rooms by selected property
  // Room type categories
  const roomTypeCategories = useMemo(() => [
    { label: 'Studio Suites', match: 'studio' },
    { label: 'One Bedroom Suites', match: 'one bedroom' },
    { label: 'Two Bedrooms One Bathroom', match: 'two bedrooms one bathroom' },
    { label: 'Two Bedrooms Two Bathrooms', match: 'two bedrooms two bathroom' },
    { label: 'Three Bedrooms One Bathroom', match: 'three bedrooms one bathroom' },
    { label: 'Three Bedrooms Two Bathrooms', match: 'three bedrooms two bathroom' },
    { label: 'Three Bedrooms Suites', match: 'three bedrooms suites' },
    { label: 'Superhero', match: 'superhero' },
    { label: 'Mansion One', match: 'mansion one' }
  ], []);
  
  // Helper function to check if a room is available for the selected dates
  const isRoomAvailable = useCallback((roomId) => {
    if (!formData.check_in || !formData.check_out) return true;
    
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    
    // Find conflicting bookings for this room
    const hasConflict = existingBookings.some(booking => {
      if (booking.room_id !== roomId) return false;
      // Skip cancelled bookings
      if (booking.status === 'cancelled' || booking.status === 'no_show') return false;
      
      const bookingCheckIn = new Date(booking.check_in);
      const bookingCheckOut = new Date(booking.check_out);
      
      // Check for date overlap
      // Overlap exists if: newCheckIn < existingCheckOut AND newCheckOut > existingCheckIn
      return checkIn < bookingCheckOut && checkOut > bookingCheckIn;
    });
    
    return !hasConflict;
  }, [formData.check_in, formData.check_out, existingBookings]);

  // Get available room types for selected property (only room types with at least one available room)
  const availableRoomTypes = useMemo(() => {
    if (!formData.property_id) return [];
    const propertyRooms = effectiveRooms.filter(r => r.property_id === formData.property_id);
    const available = [];
    
    // Check if this is the Superhero property
    const selectedProperty = effectiveProperties.find(p => p.id === formData.property_id);
    const isSuperheroProperty = selectedProperty?.name?.toLowerCase().includes('superhero');
    
    roomTypeCategories.forEach(category => {
      // For Superhero property, only show Superhero room type
      if (isSuperheroProperty && category.match !== 'superhero') {
        return;
      }
      // For non-Superhero properties, don't show Superhero room type
      if (!isSuperheroProperty && category.match === 'superhero') {
        return;
      }
      
      // Check if there's at least one available room of this type
      const hasAvailableRooms = propertyRooms.some(r => {
        const roomType = (r.room_type || r.name || '').toLowerCase().replace(/\s+/g, '');
        const matchPattern = category.match.toLowerCase().replace(/\s+/g, '');
        const matchesType = roomType.includes(matchPattern);
        
        // Also check if the room is available for the selected dates
        if (matchesType && formData.check_in && formData.check_out) {
          return isRoomAvailable(r.id);
        }
        return matchesType;
      });
      
      if (hasAvailableRooms) {
        available.push(category);
      }
    });
    
    return available;
  }, [effectiveRooms, formData.property_id, formData.check_in, formData.check_out, roomTypeCategories, effectiveProperties, isRoomAvailable]);

  // Filter rooms by property AND selected room type (only show available rooms)
  const filteredRooms = useMemo(() => {
    if (!formData.property_id || !formData.room_type) return [];
    
    // Find the matching category
    const selectedCategory = roomTypeCategories.find(c => c.label === formData.room_type);
    if (!selectedCategory) return [];
    
    return effectiveRooms
      .filter(r => {
        if (r.property_id !== formData.property_id) return false;
        const roomType = (r.room_type || r.name || '').toLowerCase().replace(/\s+/g, '');
        const matchPattern = selectedCategory.match.toLowerCase().replace(/\s+/g, '');
        const matchesType = roomType.includes(matchPattern);
        
        // Also check availability
        if (matchesType && formData.check_in && formData.check_out) {
          return isRoomAvailable(r.id);
        }
        return matchesType;
      })
      .sort((a, b) => {
        // Sort by unit name numerically
        return (a.unit_name || a.name).localeCompare(b.unit_name || b.name, undefined, { numeric: true });
      });
  }, [effectiveRooms, formData.property_id, formData.room_type, formData.check_in, formData.check_out, roomTypeCategories, isRoomAvailable]);
  
  // Get selected room details
  const selectedRoom = useMemo(() => {
    return effectiveRooms.find(r => r.id === formData.room_id);
  }, [effectiveRooms, formData.room_id]);
  
  // Calculate nights
  const nights = useMemo(() => {
    if (!formData.check_in || !formData.check_out) return 0;
    try {
      return Math.max(0, differenceInDays(parseISO(formData.check_out), parseISO(formData.check_in)));
    } catch {
      return 0;
    }
  }, [formData.check_in, formData.check_out]);
  
  // Calculate room rate from counter rates (by room_type)
  const calculatedRoomRate = useMemo(() => {
    if (!formData.room_id || !formData.check_in || !formData.check_out || nights <= 0) return 0;
    
    const selectedRoomId = formData.room_id;
    const checkInDate = parseISO(formData.check_in);
    
    // Get the selected room's room_type
    const selectedRoom = effectiveRooms.find(r => r.id === selectedRoomId);
    if (!selectedRoom) {
      console.log('Selected room not found:', selectedRoomId);
      return 0;
    }
    
    const selectedRoomType = (selectedRoom.room_type || selectedRoom.name || '').toLowerCase().trim();
    console.log('Looking for counter rate for room_type:', selectedRoomType);
    
    // Helper function to normalize room type for matching
    const normalizeForMatch = (str) => {
      return (str || '').toLowerCase()
        .replace(/type\s*[a-z]/gi, '') // Remove "Type A", "Type B", etc.
        .replace(/\([^)]*\)/g, '')     // Remove anything in parentheses
        .replace(/\s+/g, ' ')          // Normalize spaces
        .trim();
    };
    
    // Helper function to extract bedroom count
    const getBedroomInfo = (str) => {
      const lower = str.toLowerCase();
      if (lower.includes('studio')) return { bedrooms: 'studio', bathrooms: null };
      if (lower.includes('one bedroom')) return { bedrooms: 'one', bathrooms: null };
      if (lower.includes('two bedroom')) {
        const bathrooms = lower.includes('one bathroom') || lower.includes('2b1b') || lower.includes('1b') ? '1b' : 
                         lower.includes('two bathroom') || lower.includes('2b2b') || lower.includes('2b') ? '2b' : null;
        return { bedrooms: 'two', bathrooms };
      }
      if (lower.includes('three bedroom')) {
        const bathrooms = lower.includes('one bathroom') || lower.includes('3b1b') || lower.includes('1b') ? '1b' : 
                         lower.includes('two bathroom') || lower.includes('3b2b') || lower.includes('2b') ? '2b' : null;
        return { bedrooms: 'three', bathrooms };
      }
      return { bedrooms: null, bathrooms: null };
    };
    
    const selectedInfo = getBedroomInfo(selectedRoomType);
    
    // Find all rooms with matching room_type (to get their room_ids)
    const sameTypeRoomIds = effectiveRooms
      .filter(r => {
        const roomType = (r.room_type || r.name || '').toLowerCase().trim();
        const roomInfo = getBedroomInfo(roomType);
        
        // Match by bedroom count and bathroom count
        if (selectedInfo.bedrooms && roomInfo.bedrooms === selectedInfo.bedrooms) {
          if (selectedInfo.bathrooms && roomInfo.bathrooms) {
            return roomInfo.bathrooms === selectedInfo.bathrooms;
          }
          return true;
        }
        
        // Fallback: normalized string matching
        const normalizedSelected = normalizeForMatch(selectedRoomType);
        const normalizedRoom = normalizeForMatch(roomType);
        return normalizedRoom === normalizedSelected || 
               normalizedRoom.includes(normalizedSelected) || 
               normalizedSelected.includes(normalizedRoom);
      })
      .map(r => r.id);
    
    // Filter counter rates that match any of these room_ids
    const matchingRoomRates = counterRates.filter(rate => sameTypeRoomIds.includes(rate.room_id));
    
    if (matchingRoomRates.length === 0) {
      console.log('No counter rate found for room_type:', selectedRoomType, '(', selectedInfo, ') - Checked room_ids:', sameTypeRoomIds.length);
      // Return null to indicate no rate available - cannot sell
      return null;
    }
    
    // First, try to find a rate with matching date range
    let matchingRate = matchingRoomRates.find(rate => {
      if (rate.start_date && rate.end_date) {
        const rateStart = parseISO(rate.start_date);
        const rateEnd = parseISO(rate.end_date);
        return checkInDate >= rateStart && checkInDate <= rateEnd;
      }
      return false;
    });
    
    // If no date-range match, use the most recent rate (by updated_at or just first one)
    if (!matchingRate) {
      // Sort by updated_at descending to get the most recent rate
      const sortedRates = [...matchingRoomRates].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
        const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
        return dateB - dateA;
      });
      matchingRate = sortedRates[0];
      console.log('Using most recent counter rate (no date range match) for room_type:', selectedRoomType);
    }
    
    console.log('Found counter rate for room_type', selectedRoomType, '- weekday:', matchingRate.weekday_rate, 'weekend:', matchingRate.weekend_rate, 'dates:', matchingRate.start_date, 'to', matchingRate.end_date);
    
    // Calculate price for each night based on weekday/weekend
    try {
      const checkIn = parseISO(formData.check_in);
      const checkOut = parseISO(formData.check_out);
      
      // Ensure valid date range
      if (checkOut <= checkIn) return 0;
      
      // Get days excluding checkout day
      const endDate = new Date(checkOut.getTime() - 86400000);
      if (endDate < checkIn) return 0;
      
      const days = eachDayOfInterval({ start: checkIn, end: endDate });
      
      let total = 0;
      days.forEach(day => {
        const dayOfWeek = getDay(day);
        // Weekend is Friday (5) and Saturday (6)
        // Weekday is Sunday (0) to Thursday (4)
        const isWeekendDay = dayOfWeek === 5 || dayOfWeek === 6;
        total += isWeekendDay ? (matchingRate.weekend_rate || 0) : (matchingRate.weekday_rate || 0);
      });
      
      return total;
    } catch (err) {
      console.error('Error calculating room rate:', err);
      return 0;
    }
  }, [formData.room_id, formData.check_in, formData.check_out, nights, effectiveRooms, counterRates]);
  
  // Keep a ref to the current room rate for use in callbacks
  const currentRoomRateRef = useRef(0);
  useEffect(() => {
    currentRoomRateRef.current = formData.total_price || calculatedRoomRate || 0;
  }, [formData.total_price, calculatedRoomRate]);
  
  // Auto-update total_price when room rate changes
  useEffect(() => {
    if (calculatedRoomRate > 0 && formData.total_price === 0) {
      setFormData(prev => ({ ...prev, total_price: calculatedRoomRate }));
    }
  }, [calculatedRoomRate]);
  
  // Update Room Charges amount when rate changes (if Room Charges is selected)
  useEffect(() => {
    const roomRate = formData.total_price || calculatedRoomRate || 0;
    if (newChargeForm.type === 'room_charges' && roomRate > 0) {
      setNewChargeForm(prev => ({
        ...prev,
        description: 'Room Charges',
        amount: roomRate
      }));
    }
  }, [formData.total_price, calculatedRoomRate, newChargeForm.type]);
  
  // Update linked items quantities when nights changes
  useEffect(() => {
    if (nights > 0) {
      setLinkedItems(prev => ({
        ...prev,
        tourism_tax: { ...prev.tourism_tax, qty: nights },
        hotel_fee: { ...prev.hotel_fee, qty: nights }
      }));
    }
  }, [nights]);
  
  // Update room_charges rate when room rate changes
  useEffect(() => {
    const roomRate = formData.total_price || calculatedRoomRate || 0;
    setLinkedItems(prev => ({
      ...prev,
      room_charges: { 
        checked: prev.room_charges?.checked || false,
        qty: prev.room_charges?.qty || 1,
        rate: roomRate 
      }
    }));
  }, [formData.total_price, calculatedRoomRate]);
  
  // Calculate totals - use c.total (amount * quantity) for correct calculation
  const totalCharges = useMemo(() => charges.reduce((sum, c) => sum + (c.total || c.amount || 0), 0), [charges]);
  const totalPayments = useMemo(() => payments.reduce((sum, p) => sum + (p.amount || 0), 0), [payments]);
  const balance = totalCharges - totalPayments;
  
  // Initialize with pre-selected values
  useEffect(() => {
    if (open) {
      if (preSelectedRoom) {
        setFormData(prev => ({
          ...prev,
          property_id: preSelectedRoom.property_id || '',
          room_id: preSelectedRoom.id || ''
        }));
      }
      if (preSelectedDates) {
        setFormData(prev => ({
          ...prev,
          check_in: preSelectedDates.check_in || format(new Date(), 'yyyy-MM-dd'),
          check_out: preSelectedDates.check_out || format(addDays(new Date(), 1), 'yyyy-MM-dd')
        }));
      }
    }
  }, [open, preSelectedRoom, preSelectedDates]);
  
  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab('details');
      setFormData({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guest_country: 'MY',
        property_id: '',
        room_type: '',
        room_id: '',
        check_in: format(new Date(), 'yyyy-MM-dd'),
        check_out: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        total_price: 0,
        adults: 2,
        notes: '',
        e_invoice_required: false,
        einvoice_buyer_name: '',
        einvoice_buyer_tin: '',
        einvoice_buyer_id_type: 'NRIC',
        einvoice_buyer_id_value: '',
        einvoice_buyer_sst_no: 'NA',
        einvoice_buyer_email: '',
        einvoice_buyer_phone: '',
        einvoice_buyer_address: '',
        einvoice_buyer_city: '',
        einvoice_buyer_state: '',
        einvoice_buyer_postal_code: '',
        einvoice_buyer_country: 'MYS'
      });
      setCharges([]);
      setPayments([]);
      setNewChargeForm({ type: 'room_charges', description: '', amount: 0 });
      setNewPaymentForm({ method: 'cash', amount: 0, reference: '' });
      setLinkedItems({
        security_deposit: { checked: false, qty: 1, rate: 200 },
        tourism_tax: { checked: false, qty: 1, rate: 10 },
        hotel_fee: { checked: false, qty: 1, rate: 2 },
        early_checkin: { checked: false, qty: 1, rate: 15 },
        late_checkout: { checked: false, qty: 1, rate: 50 },
        printing: { checked: false, qty: 1, rate: 1 },
        vip_parking: { checked: false, qty: 1, rate: 20 },
        wheelchair: { checked: false, qty: 1, rate: 8 },
        miscellaneous: { checked: false, qty: 1, rate: 0 }
      });
    }
  }, [open]);
  
  // Handle adding charge
  const handleAddCharge = () => {
    if (!newChargeForm.amount || newChargeForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    // For miscellaneous, require description
    if (newChargeForm.type === 'miscellaneous' && (!newChargeForm.description || newChargeForm.description.trim().length < 3)) {
      toast.error('Miscellaneous charges require a description (min 3 characters)');
      return;
    }
    // Always calculate total as amount * quantity for all charge types
    const quantity = newChargeForm.quantity || 1;
    const total = newChargeForm.amount * quantity;
    
    setCharges([...charges, { ...newChargeForm, quantity, total, id: Date.now() }]);
    setNewChargeForm({ type: 'room_charges', description: '', amount: 0, quantity: 1 });
    toast.success('Charge added');
  };
  
  // Handle adding payment with linked items
  const handleAddPayment = () => {
    if (!newPaymentForm.amount || newPaymentForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Get selected charge types for linking
    const selectedChargeTypes = charges
      .filter(c => selectedChargesForPayment.includes(c.id))
      .map(c => c.type);
    
    // Get selected charge descriptions for cash reference
    const selectedChargeDescriptions = charges
      .filter(c => selectedChargesForPayment.includes(c.id))
      .map(c => c.description || c.type.replace(/_/g, ' '))
      .join(', ');
    
    const autoCashRef = selectedChargeDescriptions || 'Payment';
    
    // Add payment with linked items info
    setPayments([...payments, { 
      ...newPaymentForm, 
      id: Date.now(),
      linkedItems: selectedChargeTypes,
      linkedChargeIds: selectedChargesForPayment,
      cashReference: newPaymentForm.method === 'cash' ? autoCashRef : ''
    }]);
    
    // Reset form and selected charges
    setNewPaymentForm({ method: 'cash', amount: 0, reference: '', cashReference: '' });
    setSelectedChargesForPayment([]);
    toast.success('Payment recorded');
  };
  
  // Calculate total from linked items
  const linkedItemsTotal = useMemo(() => {
    let total = 0;
    const roomRate = formData.total_price || calculatedRoomRate || 0;
    
    Object.entries(linkedItems).forEach(([type, item]) => {
      if (item?.checked && item?.qty > 0) {
        // For room_charges, always use the current room rate
        const rate = type === 'room_charges' ? roomRate : (item?.rate || 0);
        if (rate > 0) {
          total += item.qty * rate;
        }
      }
    });
    return total;
  }, [linkedItems, formData.total_price, calculatedRoomRate]);
  
  // Auto-update payment amount when linked items change
  useEffect(() => {
    // Always update the amount when linked items total changes
    setNewPaymentForm(prev => ({ ...prev, amount: linkedItemsTotal }));
  }, [linkedItemsTotal]);
  
  // Update linked item with auto-calculation of total
  const updateLinkedItem = (type, field, value) => {
    // Get room rate synchronously at call time
    const roomRateNow = formData.total_price || calculatedRoomRate || 0;
    
    setLinkedItems(prev => {
      const newLinkedItems = {
        ...prev,
        [type]: { ...prev[type], [field]: value }
      };
      
      // Recalculate total when any checkbox is toggled
      if (field === 'checked') {
        let total = 0;
        Object.entries(newLinkedItems).forEach(([itemType, item]) => {
          if (item?.checked && item?.qty > 0) {
            const rate = itemType === 'room_charges' ? roomRateNow : (item?.rate || 0);
            if (rate > 0) {
              total += item.qty * rate;
            }
          }
        });
        console.log('updateLinkedItem - roomRateNow:', roomRateNow, 'total:', total, 'items:', JSON.stringify(newLinkedItems));
        setNewPaymentForm(prevForm => ({ ...prevForm, amount: total }));
      }
      
      return newLinkedItems;
    });
  };
  
  // Handle charge type change with auto-fill
  const handleChargeTypeChange = (type) => {
    // Use the editable room rate (formData.total_price) if set, otherwise use calculated rate
    const roomRate = formData.total_price || calculatedRoomRate || 0;
    const chargeRates = {
      room_charges: { desc: 'Room Charges', amount: roomRate, quantity: 1, unit: '', editable: false },
      security_deposit: { desc: 'Security Deposit', amount: 200, quantity: 1, unit: '', editable: false },
      tourism_tax: { desc: `Tourism Tax (RM10 x ${nights} nights)`, amount: 10 * nights, quantity: 1, unit: '', editable: false },
      hotel_fee: { desc: `Hotel Fee (RM2 x ${nights} nights)`, amount: 2 * nights, quantity: 1, unit: '', editable: false },
      early_checkin: { desc: 'Early Check In', amount: 15, quantity: 1, unit: 'hour', editable: false },
      late_checkout: { desc: 'Late Check Out', amount: 50, quantity: 1, unit: 'hour', editable: false },
      printing: { desc: 'Printing', amount: 1, quantity: 1, unit: 'paper', editable: false },
      vip_parking: { desc: 'VIP Parking', amount: 20, quantity: 1, unit: 'day', editable: false },
      wheelchair: { desc: 'Wheelchair', amount: 8, quantity: 1, unit: 'rental', editable: false },
      miscellaneous: { desc: '', amount: 0, quantity: 1, unit: '', editable: true }
    };
    const rate = chargeRates[type] || { desc: '', amount: 0, quantity: 1, unit: '', editable: true };
    setNewChargeForm({ type, description: rate.desc, amount: rate.amount, quantity: rate.quantity });
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!formData.guest_name?.trim()) {
      toast.error('Please enter guest name');
      return;
    }
    if (!formData.room_id) {
      toast.error('Please select a room');
      return;
    }
    if (!formData.check_in || !formData.check_out) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    if (formData.check_out <= formData.check_in) {
      toast.error('Check-out date must be after check-in date');
      return;
    }
    
    setSaving(true);
    try {
      const room = effectiveRooms.find(r => r.id === formData.room_id);
      
      // Determine if rate was overridden
      const finalRate = formData.total_price || calculatedRoomRate || totalCharges;
      const hasRateOverride = calculatedRoomRate > 0 && formData.total_price > 0 && formData.total_price !== calculatedRoomRate;
      const rateOverrideInfo = hasRateOverride ? {
        original_rate: calculatedRoomRate,
        final_rate: formData.total_price,
        difference: formData.total_price - calculatedRoomRate,
        override_type: formData.total_price < calculatedRoomRate ? 'discount' : 'oversale'
      } : null;
      
      // Prepare booking payload
      const bookingPayload = {
        property_id: room?.property_id || formData.property_id,
        room_id: formData.room_id,
        guest_name: formData.guest_name.trim(),
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        guest_country: formData.guest_country,
        check_in: formData.check_in,
        check_out: formData.check_out,
        total_price: finalRate,
        status: 'confirmed',
        booking_source: currentUser?.full_name || currentUser?.name || 'Direct',
        created_by: currentUser?.id || '',
        created_by_name: currentUser?.full_name || currentUser?.name || 'System',
        notes: formData.notes,
        e_invoice_required: formData.e_invoice_required,
        // Rate override info for audit trail
        rate_override: rateOverrideInfo
      };
      
      // Add e-invoice data if required
      if (formData.e_invoice_required) {
        bookingPayload.einvoice_buyer_name = formData.einvoice_buyer_name || formData.guest_name;
        bookingPayload.einvoice_buyer_tin = formData.einvoice_buyer_tin;
        bookingPayload.einvoice_buyer_id_type = formData.einvoice_buyer_id_type;
        bookingPayload.einvoice_buyer_id_value = formData.einvoice_buyer_id_value;
        bookingPayload.einvoice_buyer_sst_no = formData.einvoice_buyer_sst_no;
        bookingPayload.einvoice_buyer_email = formData.einvoice_buyer_email || formData.guest_email;
        bookingPayload.einvoice_buyer_phone = formData.einvoice_buyer_phone || formData.guest_phone;
        bookingPayload.einvoice_buyer_address = formData.einvoice_buyer_address;
        bookingPayload.einvoice_buyer_city = formData.einvoice_buyer_city;
        bookingPayload.einvoice_buyer_state = formData.einvoice_buyer_state;
        bookingPayload.einvoice_buyer_postal_code = formData.einvoice_buyer_postal_code;
        bookingPayload.einvoice_buyer_country = formData.einvoice_buyer_country;
      }
      
      // Create booking
      const bookingRes = await api.post(`${API}/bookings`, bookingPayload);
      const bookingId = bookingRes.data.id;
      
      // Create charges if any
      for (const charge of charges) {
        await api.post(`${API}/pos/charges`, {
          booking_id: bookingId,
          charge_type: charge.type,
          description: charge.description || charge.type.replace(/_/g, ' '),
          quantity: charge.quantity || 1,
          amount: charge.amount
        });
      }
      
      // Create payments if any
      for (const payment of payments) {
        await api.post(`${API}/pos/payments`, {
          booking_id: bookingId,
          payment_method: payment.method,
          amount: payment.amount,
          reference: payment.method === 'cash' ? (payment.cashReference || `Cash-${Date.now()}`) : payment.reference,
          linked_items: payment.linkedItems || [],
          notes: payment.linkedItems?.length > 0 ? `Linked to: ${payment.linkedItems.join(', ')}` : ''
        });
      }
      
      // Show prominent success message
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold text-lg">Booking Confirmed!</span>
          <span className="text-sm opacity-90">Guest: {formData.guest_name}</span>
          <span className="text-sm opacity-90">Check-in: {formData.check_in ? new Date(formData.check_in).toLocaleDateString() : '-'}</span>
          <span className="text-xs opacity-75 mt-1">Status: Confirmed</span>
        </div>,
        {
          duration: 6000,
          style: {
            background: '#059669',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          },
        }
      );
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      // Handle FastAPI validation errors which return an array of objects
      let errorMessage = 'Failed to create booking';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (Array.isArray(detail)) {
          // Pydantic validation errors - extract the msg from each error
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Use useEffect to disable all native validation after render
  React.useEffect(() => {
    if (!open) return;
    
    const modal = document.querySelector('[data-testid="create-booking-modal"]');
    if (modal) {
      // Remove required attribute from all inputs/selects
      const elements = modal.querySelectorAll('input, select, textarea, button');
      elements.forEach(el => {
        el.removeAttribute('required');
        el.setAttribute('formnovalidate', 'true');
        el.addEventListener('invalid', (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, true);
      });
      
      // Also intercept any form invalid events
      modal.addEventListener('invalid', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, true);
    }
  }, [open, activeTab]); // Re-run when tab changes
  
  if (!open) return null;
  
  // Prevent any form submission behavior
  const preventSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" 
      data-testid="create-booking-modal"
      onInvalid={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col [&_:invalid]:shadow-none [&_select]:appearance-none"
        onClick={(e) => e.stopPropagation()}
        onInvalid={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-slate-900 text-lg">New Booking</h2>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">new</span>
            </div>
            <p className="text-sm text-slate-500">
              Created by: <span className="font-medium text-slate-700">{currentUser?.full_name || currentUser?.name || 'System'}</span>
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-3 mx-4 mt-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="einvoice">E-Invoice</TabsTrigger>
            <TabsTrigger value="charges">Charges & Payments</TabsTrigger>
          </TabsList>
          
          {/* Details Tab */}
          <TabsContent value="details" className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Guest Name */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600 flex items-center gap-1">
                  <User className="h-3 w-3" /> Guest Name
                </Label>
                <Input
                  placeholder="Guest name *"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  className="h-9"
                  data-testid="guest-name-input"
                />
              </div>
              
              {/* Phone */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600 flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Phone
                </Label>
                <Input
                  placeholder="+60..."
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  className="h-9"
                />
              </div>
              
              {/* Email */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600 flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email
                </Label>
                <Input
                  placeholder="email@example.com"
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                  className="h-9"
                />
              </div>
              
              {/* Country */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Country</Label>
                <Select value={formData.guest_country} onValueChange={(v) => setFormData({ ...formData, guest_country: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Check In - MUST BE SELECTED FIRST */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Check In <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.check_in}
                  onChange={(e) => {
                    const newCheckIn = e.target.value;
                    // Auto-set check-out to next day
                    const nextDay = newCheckIn ? format(addDays(parseISO(newCheckIn), 1), 'yyyy-MM-dd') : '';
                    setFormData({ ...formData, check_in: newCheckIn, check_out: nextDay });
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-9"
                  data-testid="check-in-input"
                />
              </div>
              
              {/* Check Out - MUST BE SELECTED FIRST */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Check Out <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.check_out}
                  onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                  min={formData.check_in || new Date().toISOString().split('T')[0]}
                  className="h-9"
                  data-testid="check-out-input"
                />
              </div>
              
              {/* Property - Disabled until dates are selected */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Property</Label>
                {formData.check_in && formData.check_out ? (
                  createBookingProperties.length > 0 ? (
                    <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v, room_id: '' })}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select a property" />
                      </SelectTrigger>
                      <SelectContent>
                        {createBookingProperties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.displayName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-9 w-full items-center rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                      No properties available for selected dates
                    </div>
                  )
                ) : (
                  <div 
                    className="flex h-9 w-full items-center rounded-md border border-input bg-slate-100 px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
                    onClick={() => toast.warning('Please select Check In and Check Out dates first')}
                  >
                    Select dates first
                  </div>
                )}
              </div>
              
              {/* Room Type */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Room</Label>
                {formData.property_id && availableRoomTypes.length > 0 ? (
                  <Select 
                    value={formData.room_type} 
                    onValueChange={(v) => {
                      setFormData(prev => ({ ...prev, room_type: v, room_id: '', total_price: 0 }));
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select room type *" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoomTypes.map((rt) => (
                        <SelectItem key={rt.label} value={rt.label}>{rt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm text-muted-foreground cursor-not-allowed opacity-50">
                    {formData.property_id ? 'No room types available' : 'Select a property first'}
                  </div>
                )}
              </div>
              
              {/* Unit */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Unit</Label>
                {formData.room_type && filteredRooms.length > 0 ? (
                  <Select 
                    value={formData.room_id} 
                    onValueChange={(v) => {
                      setFormData(prev => ({ ...prev, room_id: v, total_price: 0 }));
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select unit *" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredRooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.unit_name || r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm text-muted-foreground cursor-not-allowed opacity-50">
                    {formData.room_type ? 'No units available' : 'Select a room type first'}
                  </div>
                )}
              </div>
              
              {/* Nights (Read-only) */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Nights</Label>
                <Input
                  value={nights}
                  disabled
                  className="h-9 bg-slate-100"
                />
              </div>
              
              {/* Total Charges */}
              <div className="space-y-1">
                <Label className="text-sm text-slate-600">Room Rate (Editable)</Label>
                {calculatedRoomRate === null ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 font-semibold text-sm">No rates available for this room type</p>
                    <p className="text-red-500 text-xs mt-1">Please set counter rates before creating bookings</p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">RM</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.total_price || calculatedRoomRate || 0}
                        onChange={(e) => setFormData({ ...formData, total_price: parseFloat(e.target.value) || 0 })}
                        className="h-9 pl-12 text-lg font-bold text-emerald-600"
                        data-testid="room-rate-input"
                      />
                    </div>
                    {calculatedRoomRate > 0 && (
                      <p className="text-xs text-slate-500">Based on Counter Rates (Weekday/Weekend)</p>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Price Difference Indicator - Show if rate was changed */}
            {calculatedRoomRate > 0 && formData.total_price > 0 && formData.total_price !== calculatedRoomRate && (
              <div className={`rounded-lg p-3 border ${
                formData.total_price < calculatedRoomRate 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className={`h-5 w-5 ${
                      formData.total_price < calculatedRoomRate ? 'text-red-500' : 'text-amber-500'
                    }`} />
                    <div>
                      <span className="text-sm font-medium text-slate-700">Original Rate: </span>
                      <span className="text-sm font-bold">RM {calculatedRoomRate.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full font-semibold ${
                    formData.total_price < calculatedRoomRate 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {formData.total_price < calculatedRoomRate 
                      ? `Discount: RM ${(calculatedRoomRate - formData.total_price).toFixed(2)}`
                      : `Oversale: RM ${(formData.total_price - calculatedRoomRate).toFixed(2)}`
                    }
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">* This rate change will be logged in the audit trail</p>
              </div>
            )}
            
            {/* Booking Source - Shows creator name */}
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Booking Source</Label>
              <Input
                value={currentUser?.full_name || currentUser?.name || 'Direct'}
                disabled
                className="h-9 bg-slate-100 font-medium"
              />
            </div>
            
            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Notes</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full h-20 px-3 py-2 border rounded-md text-sm resize-none"
                placeholder="Any special requests or notes..."
              />
            </div>
          </TabsContent>
          
          {/* E-Invoice Tab */}
          <TabsContent value="einvoice" className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                id="e_invoice_required"
                checked={formData.e_invoice_required}
                onCheckedChange={(checked) => setFormData({ ...formData, e_invoice_required: checked })}
              />
              <Label htmlFor="e_invoice_required" className="flex items-center gap-2 cursor-pointer">
                <FileText className="h-4 w-4" />
                Guest requires E-Invoice
              </Label>
            </div>
            
            {formData.e_invoice_required ? (
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-md">
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm">Buyer Name *</Label>
                  <Input
                    value={formData.einvoice_buyer_name}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_name: e.target.value })}
                    placeholder="Full name (defaults to guest name)"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">TIN Number *</Label>
                  <Input
                    value={formData.einvoice_buyer_tin}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_tin: e.target.value })}
                    placeholder="e.g., C12345678901"
                    className="h-9"
                  />
                  <p className="text-xs text-slate-500">Use "EI00000000010" for foreign buyers</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">ID Type *</Label>
                  <Select value={formData.einvoice_buyer_id_type} onValueChange={(v) => setFormData({ ...formData, einvoice_buyer_id_type: v })}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NRIC">NRIC (Malaysian IC)</SelectItem>
                      <SelectItem value="PASSPORT">Passport</SelectItem>
                      <SelectItem value="BRN">Business Registration No.</SelectItem>
                      <SelectItem value="ARMY">Army ID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">ID Number *</Label>
                  <Input
                    value={formData.einvoice_buyer_id_value}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_id_value: e.target.value })}
                    placeholder="ID number"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">SST Registration No.</Label>
                  <Input
                    value={formData.einvoice_buyer_sst_no}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_sst_no: e.target.value })}
                    placeholder="NA if not registered"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Email</Label>
                  <Input
                    type="email"
                    value={formData.einvoice_buyer_email}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_email: e.target.value })}
                    placeholder="Invoice email"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Phone</Label>
                  <Input
                    value={formData.einvoice_buyer_phone}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_phone: e.target.value })}
                    placeholder="Phone number"
                    className="h-9"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm">Address</Label>
                  <Input
                    value={formData.einvoice_buyer_address}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_address: e.target.value })}
                    placeholder="Street address"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">City</Label>
                  <Input
                    value={formData.einvoice_buyer_city}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_city: e.target.value })}
                    placeholder="City"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">State</Label>
                  <Select value={formData.einvoice_buyer_state} onValueChange={(v) => setFormData({ ...formData, einvoice_buyer_state: v })}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {malaysianStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Postal Code</Label>
                  <Input
                    value={formData.einvoice_buyer_postal_code}
                    onChange={(e) => setFormData({ ...formData, einvoice_buyer_postal_code: e.target.value })}
                    placeholder="Postal code"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Country Code</Label>
                  <Select value={formData.einvoice_buyer_country} onValueChange={(v) => setFormData({ ...formData, einvoice_buyer_country: v })}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {einvoiceCountryCodes.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-500 py-8">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>E-Invoice not required for this booking.</p>
                <p className="text-sm mt-1">Check the box above if guest needs an e-invoice.</p>
              </div>
            )}
          </TabsContent>
          
          {/* Charges & Payments Tab */}
          <TabsContent value="charges" className="flex-1 overflow-y-auto p-4">
            <div className="space-y-6">
              {/* Charges Section */}
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Receipt className="h-4 w-4" /> Charges
                </h3>
                
                {/* Charges List */}
                <div className="space-y-2 mb-4">
                  {charges.length === 0 ? (
                    <div className="text-sm text-slate-400 py-3 text-center border rounded-md bg-slate-50">
                      No charges added yet
                    </div>
                  ) : (
                    charges.map((charge, idx) => (
                      <div key={charge.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex-1">
                          <span className="text-sm">{charge.description || charge.type.replace(/_/g, ' ')}</span>
                          {charge.quantity > 1 && (
                            <span className="text-xs text-slate-400 ml-2">
                              (RM{charge.amount.toFixed(2)} x {charge.quantity})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">RM {(charge.total || charge.amount).toFixed(2)}</span>
                          <button
                            className="text-red-500 hover:text-red-700 text-lg font-bold leading-none"
                            onClick={() => setCharges(charges.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Add Charge Form */}
                <div className="border rounded-md p-3 bg-slate-50">
                  <Label className="text-sm font-medium mb-2 block">Add Charge</Label>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-6">
                        <Select value={newChargeForm.type} onValueChange={handleChargeTypeChange}>
                          <SelectTrigger className="h-9 w-full">
                            <SelectValue placeholder="Select charge type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="room_charges">Room Charges</SelectItem>
                            <SelectItem value="security_deposit">Security Deposit (RM200)</SelectItem>
                            <SelectItem value="tourism_tax">Tourism Tax (RM10/night)</SelectItem>
                            <SelectItem value="hotel_fee">Hotel Fee (RM2/night)</SelectItem>
                            <SelectItem value="early_checkin">Early Check In (RM15/hour)</SelectItem>
                            <SelectItem value="late_checkout">Late Check Out (RM50/hour)</SelectItem>
                            <SelectItem value="printing">Printing (RM1/paper)</SelectItem>
                            <SelectItem value="vip_parking">VIP Parking (RM20/day)</SelectItem>
                            <SelectItem value="wheelchair">Wheelchair (RM8/rental)</SelectItem>
                            <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6">
                        <Input
                          placeholder={newChargeForm.type === 'miscellaneous' ? "Description (required)" : "Description"}
                          value={newChargeForm.description}
                          onChange={(e) => setNewChargeForm({ ...newChargeForm, description: e.target.value })}
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className={['early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type) ? 'col-span-4' : 'col-span-6'}>
                        <Label className="text-xs text-slate-500">
                          {['early_checkin', 'late_checkout'].includes(newChargeForm.type) ? 'Rate/Hour (RM)' :
                           ['printing'].includes(newChargeForm.type) ? 'Rate/Paper (RM)' :
                           ['vip_parking'].includes(newChargeForm.type) ? 'Rate/Day (RM)' :
                           ['wheelchair'].includes(newChargeForm.type) ? 'Rate/Rental (RM)' :
                           'Amount (RM)'}
                        </Label>
                        <Input
                          type="number"
                          placeholder="Amount"
                          value={newChargeForm.amount || ''}
                          onChange={(e) => setNewChargeForm({ ...newChargeForm, amount: parseFloat(e.target.value) || 0 })}
                          className={`h-9 ${['room_charges', 'security_deposit', 'tourism_tax', 'hotel_fee', 'early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type) ? 'bg-slate-100 text-slate-600' : ''}`}
                          disabled={['room_charges', 'security_deposit', 'tourism_tax', 'hotel_fee', 'early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type)}
                        />
                      </div>
                      {['early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type) && (
                        <div className="col-span-4">
                          <Label className="text-xs text-slate-500">
                            {['early_checkin', 'late_checkout'].includes(newChargeForm.type) ? 'Hours' :
                             ['printing'].includes(newChargeForm.type) ? 'Papers' :
                             ['vip_parking'].includes(newChargeForm.type) ? 'Days' :
                             ['wheelchair'].includes(newChargeForm.type) ? 'Rentals' :
                             'Quantity'}
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            value={newChargeForm.quantity}
                            onChange={(e) => setNewChargeForm({ ...newChargeForm, quantity: parseInt(e.target.value) || 1 })}
                            className="h-9"
                          />
                        </div>
                      )}
                      {['early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type) && (
                        <div className="col-span-4">
                          <Label className="text-xs text-slate-500">Total (RM)</Label>
                          <div className="h-9 flex items-center px-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 font-medium">
                            {(newChargeForm.amount * newChargeForm.quantity).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {!['early_checkin', 'late_checkout', 'printing', 'vip_parking', 'wheelchair'].includes(newChargeForm.type) && (
                        <div className="col-span-6">
                          <Label className="text-xs text-slate-500">Total (RM)</Label>
                          <div className="h-9 flex items-center px-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 font-medium">
                            {(newChargeForm.amount || 0).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button type="button" className="w-full mt-3 bg-slate-800 hover:bg-slate-900" onClick={handleAddCharge}>
                    Add Charge
                  </Button>
                </div>
              </div>
              
              {/* Payments Section */}
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Payments
                </h3>
                
                {/* Existing Payments List */}
                {payments.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {payments.map((payment, idx) => (
                      <div key={payment.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex-1">
                          <span className="text-sm capitalize">{payment.method.replace(/_/g, ' ')}</span>
                          {payment.method === 'cash' && payment.cashReference && (
                            <span className="text-xs text-slate-400 ml-2">({payment.cashReference})</span>
                          )}
                          {payment.reference && payment.method !== 'cash' && (
                            <span className="text-xs text-slate-400 ml-2">({payment.reference})</span>
                          )}
                          {payment.linkedItems?.length > 0 && (
                            <div className="text-xs text-blue-500 mt-1">
                              Linked: {payment.linkedItems.map(i => i.replace(/_/g, ' ')).join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-emerald-600">RM {payment.amount.toFixed(2)}</span>
                          <button
                            className="text-red-500 hover:text-red-700 text-lg font-bold leading-none"
                            onClick={() => setPayments(payments.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Record Payment Form */}
                <div className="border rounded-md p-3 bg-slate-50">
                  <Label className="text-sm font-medium mb-2 block">Record Payment</Label>
                  
                  {/* Payment Mode and Amount */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Payment Mode</Label>
                      <Select value={newPaymentForm.method} onValueChange={(v) => setNewPaymentForm({ ...newPaymentForm, method: v })}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                          <SelectItem value="debit_card">Debit Card</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="stripe">Stripe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Amount (RM) {selectedChargesTotal > 0 && <span className="text-emerald-600">(Auto-calculated)</span>}</Label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={newPaymentForm.amount || ''}
                        onChange={(e) => setNewPaymentForm({ ...newPaymentForm, amount: parseFloat(e.target.value) || 0 })}
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  {/* Cash Reference - Auto-generated from selected charges (read-only) */}
                  {newPaymentForm.method === 'cash' && selectedChargesForPayment.length > 0 && (
                    <div className="mb-3 bg-slate-50 rounded-md p-2">
                      <Label className="text-xs text-slate-500 mb-1 block">Cash Reference (Auto-generated)</Label>
                      <div className="text-sm text-slate-700 font-medium">
                        {charges
                          .filter(c => selectedChargesForPayment.includes(c.id))
                          .map(c => c.description || c.type.replace(/_/g, ' '))
                          .join(', ') || 'Select charges below'}
                      </div>
                    </div>
                  )}
                  
                  {/* Link to Items - Show only recorded charges */}
                  <div className="mb-3">
                    <Label className="text-xs text-slate-500 mb-2 block">Link to Recorded Charges</Label>
                    <div className="border rounded-md bg-white max-h-48 overflow-y-auto">
                      {charges.length === 0 ? (
                        <div className="text-sm text-slate-400 py-4 text-center">
                          No charges recorded yet. Add charges first.
                        </div>
                      ) : (
                        charges.map((charge) => {
                          const chargeTotal = charge.total || charge.amount;
                          const isSelected = selectedChargesForPayment.includes(charge.id);
                          return (
                            <div 
                              key={charge.id} 
                              className={`flex items-center justify-between px-3 py-2 border-b last:border-0 hover:bg-slate-50 cursor-pointer ${isSelected ? 'bg-emerald-50' : ''}`}
                              onClick={() => toggleChargeForPayment(charge.id, chargeTotal)}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-slate-300"
                                  checked={isSelected}
                                  onChange={() => toggleChargeForPayment(charge.id, chargeTotal)}
                                />
                                <div>
                                  <span className="text-sm">{charge.description || charge.type.replace(/_/g, ' ')}</span>
                                  {charge.quantity > 1 && (
                                    <span className="text-xs text-slate-400 ml-1">
                                      (x{charge.quantity})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className={`text-sm font-medium ${isSelected ? 'text-emerald-600' : 'text-slate-600'}`}>
                                RM {chargeTotal.toFixed(2)}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {selectedChargesForPayment.length > 0 && (
                      <div className="mt-2 text-sm text-emerald-600 font-medium">
                        Selected: RM {selectedChargesTotal.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  {/* Reference */}
                  <div className="mb-3">
                    <Label className="text-xs text-slate-500 mb-1 block">Reference (optional)</Label>
                    <Input
                      placeholder="Reference (optional)"
                      value={newPaymentForm.reference}
                      onChange={(e) => setNewPaymentForm({ ...newPaymentForm, reference: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  
                  <Button type="button" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleAddPayment}>
                    <Plus className="h-4 w-4 mr-2" /> Record Payment
                  </Button>
                </div>
              </div>
              
              {/* Financial Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Charges:</span>
                    <span className="font-medium">RM {totalCharges.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Payments:</span>
                    <span className="font-medium text-emerald-600">RM {totalPayments.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold pt-2 border-t">
                    <span>Balance Due:</span>
                    <span className={balance > 0 ? 'text-red-600' : 'text-emerald-600'}>
                      RM {balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-white">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1" disabled>
              <FileText className="h-4 w-4" /> Invoice
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" disabled>
              <Receipt className="h-4 w-4" /> Receipt
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              formNoValidate
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={saving || !formData.guest_name || !formData.room_id || !formData.check_in || !formData.check_out || calculatedRoomRate === null}
              data-testid="submit-create-booking"
            >
              {saving ? 'Creating...' : calculatedRoomRate === null ? 'Cannot Sell - No Rates' : 'Create Booking'}
            </Button>
            <Button type="button" formNoValidate variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingModal;
